import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api, DataFile, MissingTWithDrop } from "interfaces";

import { useGetTypesForFile } from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import * as flowConfig from "flows/forecasting/config";

import {
  useGetInputFile,
  useGetTimeColumn,
  useGetTargetColumn,
  useGetGroupingColumns,
} from "./store";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();

  const inputFile = useGetInputFile();
  const colTypes = useGetTypesForFile(inputFile);
  const timeColumn = useGetTimeColumn();
  const targetColumn = useGetTargetColumn();
  const groupingColumns = useGetGroupingColumns();

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const create = useCallback(async () => {
    if (!inputFile || !timeColumn || !targetColumn) {
      setError(
        "Input Data, Time Column, and Target Column ARE ALL MANDATORY!!!"
      );
      setStatus("completed");
      return;
    }
    setStatus("in-progress");
    setError(null);
    const projectSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: { [flowConfig.INPUT_DATA]: inputFile },
      meta_data: {
        [flowConfig.TARGET_VAR_KEY]: targetColumn,
        [flowConfig.TIMESTAMP_VAR_KEY]: timeColumn,
        [flowConfig.GROUPING_VARS_KEY]: groupingColumns || [],
      },
    };
    const workspaceSpec = {
      key: "init",
      recipe: flowConfig.RECIPE_NAME,
      annotations: {
        [flowConfig.RUN_ID]: "INIT",
        [flowConfig.TIME_STAMP]: new Date().valueOf(),
      },
      input_data: { [flowConfig.INPUT_DATA]: [inputFile] },
      cleanup_spec: {
        [inputFile]: {
          column_types: {
            [targetColumn]: "float",
            [timeColumn]: ["date", "char"].includes(colTypes[timeColumn])
              ? "date"
              : "float",
          } as DataFile.UserDefinedTypes,
          missing_treatment: [
            { column: targetColumn, treatment: "drop-rows" },
            { column: timeColumn, treatment: "drop-rows" },
          ] as MissingTWithDrop[],
        },
      },
      variable_mapping: {
        [flowConfig.TARGET_VAR_KEY]: [targetColumn],
        [flowConfig.TIMESTAMP_VAR_KEY]: [timeColumn],
        [flowConfig.GROUPING_VARS_KEY]: groupingColumns || [],
      },
      model_params: {},
    };

    const resp = await createProject({
      "project-spec": projectSpec,
      "workspace-spec": workspaceSpec,
    });

    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [inputFile, timeColumn, targetColumn, groupingColumns, colTypes]);

  return [create, status, error] as const;
};
